import {
  Box,
  HStack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import {useTranslation} from 'react-i18next';
import {ModaleLayout} from '@/components/Modale/ModaleLayout';
import {useHalState} from '@synako/haligator';
import {ImagePrincipaleDuProduit} from '@/domains/mediatheque/ImagePrincipaleDuProduit';
import {DisplayLabels} from '@/domains/panier/components/DisplayLabels';
import {ListeDrapeaux} from '@/domains/panier/components/listeDrapeaux';
import {FollowPromiseOne} from '@synako/halitrek';
import {ItemCatalogue2Desc} from 'api-types/ffconnect-hal';
import {ArticleCodesDisplay} from '@/domains/panier/components/ArticleCodesDisplay';
import {FicheProduit} from '@/domains/article/FicheArticle/FicheProduit';
import {isItemStateCatalogueFF} from 'ff-common';

export interface DetailsProduitPriseDeCommandeProps {
  itemNav: FollowPromiseOne<ItemCatalogue2Desc>;
}

export const DetailsProduitPriseDeCommande = NiceModal.create(
  ({itemNav}: DetailsProduitPriseDeCommandeProps) => {
    const {t} = useTranslation();
    const modal = NiceModal.useModal();

    // Récupération des données du produit
    const itemState = useHalState(itemNav).state;
    const articleState = useHalState(itemState?.follow('article')).state;

    // Fermeture de la modale
    const onClose = () => {
      modal.remove();
    };

    if (!itemState || !articleState || !isItemStateCatalogueFF(itemState)) {
      return null;
    }

    const article = articleState.data;
    const pays = article.tolede.pays_d_origine ?? [];
    const toledeData = itemState.data.tolede;

    const allergenes = [
      ...(article.tolede.allergenes
        ?.filter((allergene) => allergene.niveau_de_contenance !== 'FREE_FROM')
        .map((allergene) => allergene.libelle) ?? []),
    ];

    const typesDeNutrimentsStates = article.tolede.type_de_nutriments;

    return (
      <ModaleLayout
        visible={modal.visible}
        hide={onClose}
        titre={t('back_office.prise_de_commande.drawer.details_produit')}
        bodyStyle={{p: {base: '0px', md: '0px'}}}
      >
        <Box
          overflow={{base: 'auto', md: 'visible'}}
          w='100%'
          pb={{base: 4, md: 0}}
          maxHeight={{base: '80vh', md: 'none'}}
        >
          <VStack
            spacing={4}
            align='stretch'
            p={2}
          >
            {/* Image et description */}
            <Box width='100%'>
              <ImagePrincipaleDuProduit
                articleState={articleState}
                itemState={itemState}
              />
            </Box>
            {/* Libelle / code article */}
            <HStack
              justifyContent='space-between'
              alignItems='flex-start'
            >
              <VStack
                align='start'
                spacing={1}
              >
                <HStack>
                  <ArticleCodesDisplay
                    code_article={article.tolede.code_article}
                    toledeData={toledeData}
                    size='xs'
                  />
                  <HStack spacing={2}>
                    <DisplayLabels articleState={articleState} />
                    <ListeDrapeaux listepays={pays} />
                  </HStack>
                </HStack>
                <Text
                  size='sm'
                  fontWeight='600'
                  color='black.text'
                >
                  {article.tolede.libelle_commercial}
                </Text>
                <Text>{article.tolede.marque?.[0]}</Text>
              </VStack>
            </HStack>
            {/* Description du produit */}
            {article.tolede.le_plus_produit && (
              <Box>
                <Text
                  fontSize='sm'
                  color='gray.700'
                >
                  {article.tolede.le_plus_produit}
                </Text>
              </Box>
            )}
            {/* Fiche produit téléchargeable */}
            <FicheProduit
              articleState={articleState}
              showDocumentIcon
            />
            {/* Table Ingredients */}
            <Box mt={4}>
              <Text
                fontSize='md'
                fontWeight='bold'
                mb={2}
              >
                {t('fiche_produit.table.ingredient_nutrition')}
              </Text>
              {typesDeNutrimentsStates && typesDeNutrimentsStates.length > 0 ? (
                <Table
                  variant='simple'
                  size='sm'
                >
                  <Thead bg='blue.main'>
                    <Tr color='white.100'>
                      <Th
                        color='white.100'
                        textTransform='capitalize'
                        textAlign='left'
                        py={1}
                        px={2}
                      >
                        {t('fiche_produit.table.informations_nutritionelles')}
                      </Th>
                      <Th
                        color='white.100'
                        textTransform='capitalize'
                        width='80px'
                        textAlign='center'
                        py={1}
                        px={2}
                        isNumeric
                      >
                        {t('fiche_produit.table.pour100gml')}
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {typesDeNutrimentsStates.map((typeDeNutriment, index) => (
                      <Tr
                        key={index}
                        bg={index % 2 === 0 ? 'white' : 'gray.light'}
                      >
                        <Td
                          py={1}
                          px={2}
                          textAlign='left'
                        >
                          {typeDeNutriment.libelle}
                        </Td>
                        <Td
                          py={1}
                          px={2}
                          textAlign='center'
                          isNumeric
                        >
                          {typeDeNutriment.quantite}
                          {typeDeNutriment.libelle !==
                          'ENER- - Valeur énergétique'
                            ? 'g'
                            : ''}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              ) : (
                <Text
                  fontSize='sm'
                  color='gray.500'
                >
                  {t('fiche_produit.table.ingredients_manquants')}
                </Text>
              )}
            </Box>

            {/* Allergenes */}
            {allergenes.length > 0 && (
              <Text
                fontSize='xs'
                fontWeight='400'
                mt={3}
              >
                <Text
                  as='span'
                  fontWeight='700'
                >
                  {t('fiche_produit.table.allergenes')}
                </Text>{' '}
                {allergenes.join(', ')}
              </Text>
            )}
          </VStack>
        </Box>
      </ModaleLayout>
    );
  },
);
